import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { History, Undo2 } from "lucide-react";
import ImportReviewScreen from "./ImportReviewScreen";

type ImportRow = {
  id: string;
  filename: string;
  source_name: string;
  source_person: string | null;
  uploaded_by: string | null;
  status: string;
  records_detected: number;
  profiles_updated: number;
  profiles_created: number;
  conflicts_found: number;
  records_skipped: number;
  created_at: string;
  applied_at: string | null;
  rolled_back_at: string | null;
};

export default function ImportHistoryTable() {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [rollbackFor, setRollbackFor] = useState<ImportRow | null>(null);
  const [rollbackPlan, setRollbackPlan] = useState<any | null>(null);
  const [rollbackBusy, setRollbackBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("instructor_imports")
      .select("*").order("created_at", { ascending: false }).limit(50);
    setRows((data ?? []) as any);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel(`import-history-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "instructor_imports" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const openRollback = async (row: ImportRow) => {
    setRollbackFor(row); setRollbackPlan(null);
    const { data, error } = await supabase.functions.invoke("rollback-instructor-import", {
      body: { import_id: row.id, dry_run: true },
    });
    if (error) { toast.error(error.message); return; }
    setRollbackPlan(data);
  };

  const doRollback = async () => {
    if (!rollbackFor) return;
    setRollbackBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("rollback-instructor-import", {
        body: { import_id: rollbackFor.id, dry_run: false },
      });
      if (error) throw error;
      toast.success(`Rolled back ${data.reverted}. ${data.flagged} item(s) flagged as manually modified.`);
      setRollbackFor(null); setRollbackPlan(null);
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Rollback failed");
    } finally {
      setRollbackBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <History className="h-4 w-4" />
          Import History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">No imports yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>File</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Applied</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs font-mono truncate max-w-[180px]" title={r.filename}>{r.filename}</TableCell>
                    <TableCell className="text-xs">{r.source_name}{r.source_person ? ` (${r.source_person})` : ""}</TableCell>
                    <TableCell><Badge variant="outline">{r.status.replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-xs">{r.records_detected}</TableCell>
                    <TableCell className="text-xs">
                      {r.profiles_updated} upd / {r.profiles_created} new / {r.records_skipped} skip
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {(r.status === "awaiting_review" || r.status === "partially_applied") && (
                          <Button size="sm" variant="outline" onClick={() => setReviewId(r.id)}>Review</Button>
                        )}
                        {(r.status === "applied" || r.status === "partially_applied") && !r.rolled_back_at && (
                          <Button size="sm" variant="ghost" onClick={() => openRollback(r)}>
                            <Undo2 className="h-3 w-3 mr-1" />Roll back
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {reviewId && <ImportReviewScreen importId={reviewId} onClose={() => { setReviewId(null); load(); }} />}

      {rollbackFor && (
        <Dialog open onOpenChange={(o) => { if (!o) { setRollbackFor(null); setRollbackPlan(null); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Roll back import</DialogTitle>
              <DialogDescription>
                {rollbackFor.filename} — this will attempt to reverse changes made by this import.
                Items modified after the import will be flagged and left as-is.
              </DialogDescription>
            </DialogHeader>
            {!rollbackPlan ? (
              <div className="text-sm text-muted-foreground py-4">Loading plan…</div>
            ) : (
              <div className="space-y-2 text-sm">
                <div>Will revert: <b>{rollbackPlan.will_revert}</b></div>
                <div>Will flag as un-revertable (manually modified): <b>{rollbackPlan.will_flag}</b></div>
                <div className="max-h-64 overflow-y-auto text-xs border rounded p-2">
                  {(rollbackPlan.plan ?? []).map((p: any, i: number) => (
                    <div key={i} className={`py-0.5 ${p.can_revert ? "" : "text-amber-700"}`}>
                      {p.can_revert ? "↩" : "⚠"} {p.table}{p.column ? `.${p.column}` : ""} — {p.kind}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRollbackFor(null); setRollbackPlan(null); }} disabled={rollbackBusy}>Cancel</Button>
              <Button onClick={doRollback} disabled={rollbackBusy || !rollbackPlan}>
                {rollbackBusy ? "Rolling back…" : "Roll back"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
